import { ActionType, EventId, RegionId, TaskTypeId } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { browserTimeZone } from "components/calendar/calendar-utils";
import { trpc } from "components/rpc/trpc";
import {
    activityTypeChoicesForRecordType,
    byActivityTypeName,
    chooseActivityTypeLabel,
    defaultActivityTypeId,
    noActivityTypeKey,
    useActivityTypeOptions,
} from "components/tasks/activity-type-options";
import { DatePicker } from "components/ui/date-time-picker/date-picker";
import { TimePicker } from "components/ui/date-time-picker/time-picker";
import { InlineBanner } from "components/ui/inline-banner";
import { InternalLink } from "components/ui/internal-link";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import React, { useCallback, useMemo, useState } from "react";
import {
    useCurrentUserId,
    useIsAllowedInWholeTenant,
    useRegionId,
} from "store/selectors/hooks";
import { stableQueryOptionsRpc } from "util/requests";
import { instantToLocalParts, localPartsToInstant } from "./event-time";
import { newEventDefaults } from "./new-event-defaults";
import {
    addEventTypesLinkText,
    askAdministratorText,
    noEventTypesText,
} from "./new-event-zero-state";

const EVENT_TIME_GRANULARITY_MINUTES = 5;

interface NewEventPanelProps {
    onCreated: (eventId: EventId) => void;
    onClose: () => void;
}

export const NewEventPanel: React.FC<NewEventPanelProps> = props => {
    const { onCreated, onClose } = props;
    const regionId = useRegionId();
    const currentUserId = useCurrentUserId();
    const trpcClient = trpc.useTRPC();
    const options = useActivityTypeOptions();
    const eventTypes = useMemo(
        () =>
            activityTypeChoicesForRecordType(options, "event", null, null).sort(
                byActivityTypeName,
            ),
        [options],
    );
    const timeZone = useMemo(() => browserTimeZone(), []);
    const defaults = useMemo(() => newEventDefaults(timeZone, new Date()), [timeZone]);
    const defaultParts = useMemo(
        () => instantToLocalParts(defaults.startsAt.toISOString(), timeZone),
        [defaults, timeZone],
    );
    const [selectedTypeId, setSelectedTypeId] = useState<TaskTypeId | null>(null);
    const soleTypeId = defaultActivityTypeId(options, "event", null);
    const chosenType = useMemo(
        () =>
            eventTypes.find(option => option.id === (selectedTypeId ?? soleTypeId)) ??
            null,
        [eventTypes, selectedTypeId, soleTypeId],
    );
    const handleTypeChange = useCallback(
        (value: string) =>
            setSelectedTypeId(value === noActivityTypeKey ? null : (value as TaskTypeId)),
        [],
    );
    const [dateValue, setDateValue] = useState(defaultParts.date);
    const [timeValue, setTimeValue] = useState(defaultParts.time);
    const [error, setError] = useState<string | null>(null);

    const createMutation = useMutation({
        ...trpcClient.event.create.mutationOptions(),
        onSuccess: result => onCreated(result.id),
        onError: () => setError("Could not create the event. Please try again."),
    });

    const handleCreate = useCallback(() => {
        if (!chosenType) {
            return;
        }
        setError(null);
        createMutation.mutate({
            regionId: regionId ?? ("" as RegionId),
            activityTypeId: chosenType.id,
            title: chosenType.name,
            description: null,
            schedule: {
                startsAt: localPartsToInstant(dateValue, timeValue, timeZone),
                timeZone,
                eventDuration: defaults.eventDuration,
            },
            address: {
                location: null,
                street: null,
                city: null,
                state: null,
                zip: null,
            },
            ownerUserIds: currentUserId ? [currentUserId] : [],
            invitees: [],
        });
    }, [
        createMutation,
        regionId,
        currentUserId,
        chosenType,
        dateValue,
        timeValue,
        timeZone,
        defaults,
    ]);

    return (
        <Panel
            isOpen={true}
            type={PanelType.medium}
            headerText="New event"
            onDismiss={onClose}>
            <div className="space-y-3 p-4">
                {error ? <InlineBanner type="error">{error}</InlineBanner> : null}
                {eventTypes.length === 0 ? (
                    <NoEventTypes />
                ) : (
                    <>
                        <Select
                            label="Event type"
                            value={chosenType?.id ?? noActivityTypeKey}
                            disabled={createMutation.isPending}
                            onChange={e => handleTypeChange(e.target.value)}>
                            {chosenType ? null : (
                                <option value={noActivityTypeKey}>
                                    {chooseActivityTypeLabel}
                                </option>
                            )}
                            {eventTypes.map(option => (
                                <option key={option.id} value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </Select>
                        <div className="grid grid-cols-2 gap-3">
                            <DatePicker
                                label="Date"
                                value={dateValue}
                                onChange={value => {
                                    if (value) {
                                        setDateValue(value);
                                    }
                                }}
                                onOpenChange={() => undefined}
                                disabled={createMutation.isPending}
                                required={true}
                                textSize="base"
                                minValue={null}
                                maxValue={null}
                                visibleYears={20}
                            />
                            <TimePicker
                                label="Start time"
                                value={timeValue}
                                onChange={value => {
                                    if (value) {
                                        setTimeValue(value);
                                    }
                                }}
                                disabled={createMutation.isPending}
                                nativeStepMinutes={EVENT_TIME_GRANULARITY_MINUTES}
                            />
                        </div>
                        <p className="text-xs text-gray-500">
                            The title, place, duration, and guest list are set on the
                            event page.
                        </p>
                        <div className="flex items-center justify-end gap-2 border-t border-gray-200 pt-3">
                            <SecondaryButton
                                disabled={createMutation.isPending}
                                onClick={onClose}>
                                Cancel
                            </SecondaryButton>
                            <PrimaryButton
                                disabled={chosenType === null || createMutation.isPending}
                                onClick={handleCreate}>
                                Create event
                            </PrimaryButton>
                        </div>
                    </>
                )}
            </div>
        </Panel>
    );
};

const NoEventTypes: React.FC = () => {
    const canManageAgencySettings = useIsAllowedInWholeTenant(ActionType.UpdateTenant);
    return (
        <div className="space-y-2">
            <p className="text-sm text-gray-900">{noEventTypesText}</p>
            {canManageAgencySettings ? (
                <p className="text-sm">
                    <InternalLink to="/settings/agency/tasks">
                        {addEventTypesLinkText}
                    </InternalLink>
                </p>
            ) : (
                <AskAdministrator />
            )}
        </div>
    );
};

const AskAdministrator: React.FC = () => {
    const trpcClient = trpc.useTRPC();
    const query = useQuery({
        ...trpcClient.user.administrators.queryOptions(),
        ...stableQueryOptionsRpc(),
        staleTime: 5 * 60 * 1000,
    });
    const names = (query.data ?? []).map(administrator => administrator.name);
    return <p className="text-sm text-gray-600">{askAdministratorText(names)}</p>;
};
